import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workQueueService } from "@/core/api/services";
import { workbenchKeys } from "@/core/api/queryKeys";

export function useWorkItemActions() {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: workbenchKeys.workItems.all });
    queryClient.invalidateQueries({ queryKey: workbenchKeys.stats.all });
  };

  const claim = useMutation({
    mutationFn: async (id: string) => {
      const response = await workQueueService.claimWorkItem({ id });
      return response.item;
    },
    onSuccess: () => invalidateAll(),
  });

  const release = useMutation({
    mutationFn: async (id: string) => {
      const response = await workQueueService.releaseWorkItem({ id });
      return response.item;
    },
    onSuccess: () => invalidateAll(),
  });

  const transition = useMutation({
    mutationFn: async ({ id, newStatus }: { id: string; newStatus: string }) => {
      const response = await workQueueService.transitionStatus({ id, newStatus });
      return response.item;
    },
    onSuccess: () => invalidateAll(),
  });

  const addNote = useMutation({
    mutationFn: async ({ itemId, note }: { itemId: string; note: string }) => {
      await workQueueService.addNote({ itemId, note });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workbenchKeys.workItems.all });
    },
  });

  const logTime = useMutation({
    mutationFn: async ({
      itemId,
      startTime,
      endTime,
    }: {
      itemId: string;
      startTime: Date;
      endTime: Date;
    }) => {
      const { timestampFromDate } = await import("@bufbuild/protobuf/wkt");
      await workQueueService.logTime({
        itemId,
        startTime: timestampFromDate(startTime),
        endTime: timestampFromDate(endTime),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workbenchKeys.workItems.all });
    },
  });

  return {
    claim: claim.mutateAsync,
    claiming: claim.isPending,
    release: release.mutateAsync,
    releasing: release.isPending,
    transition: transition.mutateAsync,
    transitioning: transition.isPending,
    addNote: addNote.mutateAsync,
    addingNote: addNote.isPending,
    logTime: logTime.mutateAsync,
    loggingTime: logTime.isPending,
  };
}
